/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Link, makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Breadcrumb from '../../../components/breadcrumbs/Breadcrumbs';
import {TertiaryButton} from '../../../components/buttons/TertiaryButton';
import useMedia from '../../../hooks/useMedia';
import {NavigationIcon} from '../../../icons/NavigationIcon';
import {ScreenShotIcon} from '../../../icons/ScreenShotIcon';
import {dlsColor} from '../../../theme/theme';
import {Color, SavedMapViewType} from '../../../types/types';
import {HeaderTypography} from '../../../typography/HeaderTypography';
import {LabelTypography} from '../../../typography/LabelTypography';
import {shortenName} from './SavedMapViewsChooser';

interface SavedViewsHeaderProps {
  isSaveMapForm: boolean;
  savedMapViews?: SavedMapViewType[];
  resetToolState?: Function;
  MapControlsBreadCrumbs?: React.ReactElement<any, any>;
  mapViewToEdit?: SavedMapViewType;
  openSaveMapForm?: Function;
  showHeaderAlways?: boolean;
};

const useStyles = makeStyles(theme => ({
  headerContainer : {
    display         : 'flex',
    justifyContent  : 'space-between',
    alignItems      : 'center',
    backgroundColor : dlsColor('grey10'),
    borderRadius    : '4px 4px 0px 0px'
  },
  breadcrumb : {
    height        : '25px',
    display       : 'flex',
    textTransform : 'uppercase'
  },
  secondaryBreadcrumbLabel : {
    fontSize : '10px !important'
  },
  inactiveCrumb : {
    color  : dlsColor('white'),
    cursor : 'pointer'
  },
  activeCrumb : {
    color : dlsColor('grey5')
  },
  header : {
    padding    : '6px 0 6px 12px',
    fontSize   : '12px !important',
    color      : dlsColor('white'),
    lineHeight : '16px !important'
  },
  currentView : {
    display    : 'flex',
    alignItems : 'center'
  },
  currentViewIcon : {
    marginTop : 15
  },
  breadcrumbSeparator : {
    color : dlsColor('white')
  }
}));

export const SavedMapViewsChooserHeader = ({
  isSaveMapForm,
  MapControlsBreadCrumbs,
  resetToolState,
  savedMapViews,
  mapViewToEdit,
  openSaveMapForm,
  showHeaderAlways
}: SavedViewsHeaderProps) => {
  const classes     = useStyles();
  const {isDesktop} = useMedia();
  const showHeader = !MapControlsBreadCrumbs && (!isSaveMapForm || !savedMapViews.length);
  const showBreadcrumbs =  isSaveMapForm && savedMapViews.length !== 0;
  const showSaveForm = !isSaveMapForm && savedMapViews.length !== 0;

  return (
    <div className={classes.headerContainer}>
      {MapControlsBreadCrumbs && <MapControlsBreadCrumbs isPlainCrumb crumb={translate('maps:mapViews.header')} />}
      {showHeader && (
        <HeaderTypography variant='h4' className={classes.header}>
          {translate('maps:mapViews.header')}
        </HeaderTypography>
      )}
      {(isDesktop || (showHeaderAlways && !MapControlsBreadCrumbs)) && showBreadcrumbs && (
        <div className={classes.breadcrumb}>
          <Breadcrumb height={'10px'}
            iconClick={resetToolState}
            color={Color.white}
            separator={(
              <div className={classes.breadcrumbSeparator}>
                -
              </div>
            )}
            className={classes.breadcrumb}
            icon={<NavigationIcon direction='back' />}>
            <LabelTypography>
              <Link onClick={resetToolState} className={classes.inactiveCrumb}>
                {translate('maps:mapViews.breadcrumb')}
              </Link>
            </LabelTypography>
            <LabelTypography className={classes.secondaryBreadcrumbLabel}>
              <span className={classes.activeCrumb}>
                {mapViewToEdit
                  ? translate('maps:mapViews.editingBreadcrumb', {mapView: shortenName(mapViewToEdit?.id)})
                  : translate('maps:mapViews.secondaryBreadcrumb')}
              </span>
            </LabelTypography>
          </Breadcrumb>
        </div>
      )}
      {showSaveForm && (
        <TertiaryButton style={{height: '25px'}} isAlt fontSize={'12px'} noUnderline onClick={openSaveMapForm}>
          <div className={classes.currentView}>
            <div className={classes.currentViewIcon}>
              <ScreenShotIcon color={Color.white}/>
            </div>
            {translate('maps:mapViews.save')}
          </div>
        </TertiaryButton>
      )}
    </div>
  );
};

export default SavedMapViewsChooserHeader;
